import { createClient } from '@supabase/supabase-js';
import { IS_TEST_MODE, TABLES, TEST_MODE_CONFIG, OPENAI_SERVER_URL } from '../constants';
import { Assistant, AssistantStats, DashboardData, InviteCode, InviteCodeUsage, WaitlistEntry, PersonalContextEntry } from '../types';
import { analyzeQuestionsWithAI } from './openai';
import { ContextCard } from './openai';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// Export test mode for backward compatibility
export const isTestMode = IS_TEST_MODE;

// Check if OpenAI server is available
export const checkOpenAIServer = async (): Promise<boolean> => {
    try {
        const response = await fetch(`${OPENAI_SERVER_URL}/health`);
        return response.ok;
    } catch {
        return false;
    }
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Export tables for backward compatibility
export { TABLES };

// File upload functions
export const uploadBackgroundImage = async (file: File, userId: string): Promise<string | null> => {
    if (isTestMode) {
        return 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80';
    }

    try {
        const fileExt = file.name.split('.').pop();
        const fileName = `${userId}/background-${Date.now()}.${fileExt}`;

        const { data, error } = await supabase.storage
            .from('assistant-images')
            .upload(fileName, file, {
                cacheControl: '3600',
                upsert: false
            });

        if (error) {
            console.error('Error uploading file:', error);
            throw error;
        }

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
            .from('assistant-images')
            .getPublicUrl(fileName);

        return publicUrl;
    } catch (error) {
        console.error('Error uploading background image:', error);
        return null;
    }
};

export const updateAssistantBackgroundImage = async (userId: string, backgroundImageUrl: string): Promise<boolean> => {
    if (isTestMode) {
        console.log('Test mode: Background image would be updated');
        return true;
    }

    try {
        const { error } = await supabase
            .from(TABLES.ASSISTANTS)
            .update({ background_image_url: backgroundImageUrl })
            .eq('user_id', userId);

        if (error) throw error;
        return true;
    } catch (error) {
        console.error('Error updating background image:', error);
        return false;
    }
};

// Export mock data for backward compatibility
export const mockAssistants = [...TEST_MODE_CONFIG.MOCK_ASSISTANTS];

// New functions for single assistant approach
export const getUserAssistant = async (userId: string): Promise<Assistant | null> => {
    if (isTestMode) {
        const mockAssistant = {
            ...TEST_MODE_CONFIG.MOCK_ASSISTANT,
            personal_context_entries: [
                {
                    id: '1',
                    assistant_id: TEST_MODE_CONFIG.MOCK_ASSISTANT.id,
                    title: 'Bio',
                    content: 'Professional software engineer and AI enthusiast with 10+ years of experience in web development.',
                    display_order: 1,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                },
                {
                    id: '2',
                    assistant_id: TEST_MODE_CONFIG.MOCK_ASSISTANT.id,
                    title: 'Experience & Expertise',
                    content: 'Full-stack developer specializing in React, Node.js, and cloud technologies. Passionate about creating innovative AI solutions.',
                    display_order: 2,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                },
                {
                    id: '3',
                    assistant_id: TEST_MODE_CONFIG.MOCK_ASSISTANT.id,
                    title: 'Testimonials',
                    content: 'Lauren is an excellent developer who consistently delivers high-quality work and brings creative solutions to complex problems.',
                    display_order: 3,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                },
            ],
        };
        return mockAssistant;
    }

    try {
        const { data, error } = await supabase
            .from('assistants_with_context')
            .select('*')
            .eq('user_id', userId)
            .single();

        if (error) {
            if (error.code === 'PGRST116') { // No rows returned
                return null;
            }
            throw error;
        }

        return data;
    } catch (error) {
        console.error('Error fetching user assistant:', error);
        return null;
    }
};

export const getAssistantStats = async (assistantId: string): Promise<AssistantStats> => {
    if (isTestMode) {
        return {
            totalConversations: 12,
            lastConversationDate: new Date().toISOString(),
            totalexchanges: 24,
        };
    }

    try {
        // Get all conversations for the assistant
        const { data: conversations, error } = await supabase
            .from(TABLES.CONVERSATIONS)
            .select('session_id, created_at')
            .eq('assistant_id', assistantId)
            .order('created_at', { ascending: false });

        if (error) {
            console.error('Error fetching conversations:', error);
            throw error;
        }

        // Handle case where session_id might not be properly populated
        let totalConversations: number;
        if (conversations && conversations.length > 0) {
            // Check if session_id is properly populated
            const hasValidSessionIds = conversations.every(c => c.session_id && c.session_id !== '');

            if (hasValidSessionIds) {
                // Count unique sessions (conversations)
                const uniqueSessions = new Set(conversations.map(c => c.session_id));
                totalConversations = uniqueSessions.size;
            } else {
                // Fallback: count each conversation as a separate session if session_id is not properly set
                totalConversations = conversations.length;
            }
        } else {
            totalConversations = 0;
        }

        // Get the latest conversation date
        const lastConversationDate = conversations?.[0]?.created_at;

        // Count total exchanges (each conversation row represents 1 exchange: user + assistant)
        const totalexchanges = conversations?.length || 0;

        return {
            totalConversations,
            lastConversationDate,
            totalexchanges,
        };
    } catch (error) {
        console.error('Error fetching assistant stats:', error);
        return {
            totalConversations: 0,
            totalexchanges: 0,
        };
    }
};

export interface QuestionAnalysis {
    categories: {
        category: string;
        count: number;
        examples: string[];
    }[];
    commonThemes: string[];
    insights: string[];
}

export const analyzeQuestions = async (questions: string[], assistantName: string): Promise<QuestionAnalysis> => {
    if (isTestMode) {
        // Simulate AI-generated analysis for test mode
        const mockAnalysis = {
            categories: [
                {
                    category: "Service Inquiries",
                    count: 4,
                    examples: ["What services do you offer?", "Do you provide coaching?", "What types of consultations do you do?", "Can you help with specific problems?"]
                },
                {
                    category: "Pricing & Cost",
                    count: 3,
                    examples: ["What are your rates?", "How much do you charge per session?", "What are your rates for coaching sessions?"]
                },
                {
                    category: "Availability & Scheduling",
                    count: 2,
                    examples: ["Do you offer remote consultations?", "Are you available on weekends?"]
                },
                {
                    category: "Background & Expertise",
                    count: 1,
                    examples: ["What makes you different from other coaches?"]
                }
            ],
            commonThemes: ["Service exploration", "Cost considerations", "Convenience factors", "Credibility assessment"],
            insights: [
                "Visitors are primarily focused on understanding your service offerings and pricing structure",
                "There's significant interest in remote/online consultation options",
                "People are evaluating your unique value proposition compared to competitors",
                "Weekend availability is a key consideration for potential clients"
            ]
        };
        return mockAnalysis;
    }

    if (!questions || questions.length === 0) {
        return {
            categories: [],
            commonThemes: [],
            insights: []
        };
    }

    // Use the openai service for AI analysis
    return await analyzeQuestionsWithAI(questions, assistantName);
};

export const getSampleQuestions = async (assistantId: string, limit: number = 10): Promise<string[]> => {
    if (isTestMode) {
        return [
            "What services do you offer?",
            "How can I get started with your consultation?",
            "What are your rates for coaching sessions?",
            "Do you offer remote consultations?",
            "What makes you different from other coaches?",
            "What is your experience in this field?",
            "How much do you charge per session?",
            "Are you available on weekends?",
            "What qualifications do you have?",
            "Can you help with specific problems?"
        ];
    }

    try {
        const { data: conversations, error } = await supabase
            .from(TABLES.CONVERSATIONS)
            .select('user_message')
            .eq('assistant_id', assistantId)
            .order('created_at', { ascending: false })
            .limit(limit);

        if (error) {
            console.error('Error fetching sample questions:', error);
            return [];
        }

        // Extract unique questions and clean them up
        const questions = conversations
            ?.map(c => c.user_message?.trim())
            .filter((msg, index, arr) => msg && arr.indexOf(msg) === index) // Remove duplicates
            .filter(msg => msg.length > 10 && msg.length < 200) // Filter reasonable length questions
            .slice(0, limit) || [];

        return questions;
    } catch (error) {
        console.error('Error fetching sample questions:', error);
        return [];
    }
};

// Cache duration in milliseconds (24 hours)
const ANALYSIS_CACHE_DURATION = 24 * 60 * 60 * 1000;

// Minimum questions needed to run analysis
const MIN_QUESTIONS_FOR_ANALYSIS = 3;

// Minimum new questions needed to trigger re-analysis
const MIN_NEW_QUESTIONS_FOR_REANALYSIS = 5;

export const getCachedQuestionAnalysis = async (assistantId: string): Promise<QuestionAnalysis | null> => {
    if (isTestMode) {
        console.log('Test mode: Skipping cached analysis fetch');
        return null;
    }

    try {
        const { data, error } = await supabase
            .from(TABLES.QUESTION_ANALYSIS_CACHE)
            .select('*')
            .eq('assistant_id', assistantId)
            .single();

        if (error || !data) return null;

        // Check if cache is still valid
        const cacheAge = Date.now() - new Date(data.updated_at).getTime();
        if (cacheAge > ANALYSIS_CACHE_DURATION) {
            return null; // Cache expired
        }

        return data.analysis as QuestionAnalysis;
    } catch (error) {
        console.error('Error fetching cached analysis:', error);
        return null;
    }
};

export const saveQuestionAnalysisCache = async (assistantId: string, analysis: QuestionAnalysis, questionCount: number): Promise<void> => {
    if (isTestMode) {
        console.log('Test mode: Skipping analysis cache save');
        return;
    }

    try {
        const { error } = await supabase
            .from(TABLES.QUESTION_ANALYSIS_CACHE)
            .upsert({
                assistant_id: assistantId,
                analysis: analysis,
                question_count: questionCount,
                updated_at: new Date().toISOString()
            });

        if (error) {
            console.error('Error saving analysis cache:', error);
        }
    } catch (error) {
        console.error('Error saving analysis cache:', error);
    }
};

export const getNewQuestionsCount = async (assistantId: string, currentQuestions: string[]): Promise<number> => {
    if (isTestMode) {
        console.log('Test mode: Returning 0 for new questions count');
        return 0;
    }

    try {
        const { data, error } = await supabase
            .from(TABLES.QUESTION_ANALYSIS_CACHE)
            .select('question_count')
            .eq('assistant_id', assistantId)
            .single();

        if (error || !data) return currentQuestions.length; // No cache, all questions are "new"

        const lastQuestionCount = data.question_count || 0;
        return Math.max(0, currentQuestions.length - lastQuestionCount);
    } catch (error) {
        console.error('Error getting new questions count:', error);
        return 0;
    }
};

export const shouldRunAnalysis = async (assistantId: string, currentQuestions: string[]): Promise<boolean> => {
    if (isTestMode) {
        console.log('Test mode: Should run analysis = true');
        return true;
    }
    if (currentQuestions.length < MIN_QUESTIONS_FOR_ANALYSIS) return false;

    try {
        // Check if we have cached analysis
        const cachedAnalysis = await getCachedQuestionAnalysis(assistantId);
        if (!cachedAnalysis) return true; // No cache, run analysis

        // Get the last analysis timestamp
        const { data, error } = await supabase
            .from(TABLES.QUESTION_ANALYSIS_CACHE)
            .select('updated_at, question_count')
            .eq('assistant_id', assistantId)
            .single();

        if (error || !data) return true;

        // Check if enough new questions have been added to warrant re-analysis
        const lastQuestionCount = data.question_count || 0;
        const newQuestionsCount = currentQuestions.length - lastQuestionCount;

        if (newQuestionsCount >= MIN_NEW_QUESTIONS_FOR_REANALYSIS) {
            return true; // Enough new questions, run analysis
        }

        return false; // Use cached analysis
    } catch (error) {
        console.error('Error checking if should run analysis:', error);
        return true; // Run analysis on error to be safe
    }
};

export const forceRefreshQuestionAnalysis = async (assistantId: string, assistantName: string): Promise<QuestionAnalysis | null> => {
    const sampleQuestions = await getSampleQuestions(assistantId, 10);

    if (sampleQuestions.length < MIN_QUESTIONS_FOR_ANALYSIS) {
        return null;
    }

    console.log('Force refreshing AI analysis for assistant:', assistantName);
    const questionAnalysis = await analyzeQuestions(sampleQuestions, assistantName);

    if (questionAnalysis) {
        await saveQuestionAnalysisCache(assistantId, questionAnalysis, sampleQuestions.length);
    }

    return questionAnalysis;
};

export const getDashboardData = async (userId: string): Promise<DashboardData> => {
    const assistant = await getUserAssistant(userId);

    if (!assistant) {
        return {
            assistant: undefined,
            stats: {
                totalConversations: 0,
                totalexchanges: 0,
            },
            hasAssistant: false,
        };
    }

    const stats = await getAssistantStats(assistant.id);
    const sampleQuestions = await getSampleQuestions(assistant.id, 10);

    // Smart analytics: only run when necessary
    let questionAnalysis: QuestionAnalysis | undefined;

    if (sampleQuestions.length > 0) {
        const shouldAnalyze = await shouldRunAnalysis(assistant.id, sampleQuestions);

        if (shouldAnalyze) {
            console.log('Running AI analysis for assistant:', assistant.name);
            questionAnalysis = await analyzeQuestions(sampleQuestions, assistant.name);

            // Cache the results
            if (questionAnalysis) {
                await saveQuestionAnalysisCache(assistant.id, questionAnalysis, sampleQuestions.length);
            }
        } else {
            console.log('Using cached analysis for assistant:', assistant.name);
            questionAnalysis = await getCachedQuestionAnalysis(assistant.id) || undefined;
        }
    }

    return {
        assistant,
        stats,
        hasAssistant: true,
        sampleQuestions,
        questionAnalysis,
    };
};

export const createOrUpdateAssistant = async (
    userId: string,
    assistantData: Omit<Assistant, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'personal_context_entries'>,
    isUpdate: boolean = false,
    shouldRegenerateCache: boolean = true
): Promise<Assistant | null> => {
    if (isTestMode) {
        console.log('Test mode: Assistant would be', isUpdate ? 'updated' : 'created');
        return TEST_MODE_CONFIG.MOCK_ASSISTANT;
    }

    try {
        if (isUpdate) {
            const { data, error } = await supabase
                .from(TABLES.ASSISTANTS)
                .update(assistantData)
                .eq('user_id', userId)
                .select()
                .single();

            if (error) throw error;

            // Clear context cards cache when assistant is updated
            if (data && shouldRegenerateCache) {
                await clearContextCardsCache(data.id);
            }

            return data;
        } else {
            const { data, error } = await supabase
                .from(TABLES.ASSISTANTS)
                .insert([{ ...assistantData, user_id: userId }])
                .select()
                .single();

            if (error) throw error;
            return data;
        }
    } catch (error) {
        console.error('Error creating/updating assistant:', error);
        return null;
    }
};

// Invite Code Functions
export const validateInviteCode = async (code: string, userId?: string): Promise<InviteCode | null> => {
    if (isTestMode) {
        // Mock validation for test mode
        if (['TEST123', 'UNLIMITED', 'MULTI01'].includes(code)) {
            return {
                id: 'test-invite-id',
                code: code,
                current_uses: 0,
                max_uses: code === 'UNLIMITED' ? null : (code === 'MULTI01' ? 5 : 1),
                created_at: new Date().toISOString(),
            };
        }
        return null;
    }

    try {
        const { data, error } = await supabase
            .from(TABLES.INVITE_CODES)
            .select('*')
            .eq('code', code.toUpperCase())
            .single();

        if (error) {
            if (error.code === 'PGRST116') { // No rows returned
                return null;
            }
            throw error;
        }

        // Check if code has expired
        if (data.expires_at && new Date(data.expires_at) < new Date()) {
            return null;
        }

        // Check if code has reached max uses (null = unlimited)
        if (data.max_uses !== null && data.current_uses >= data.max_uses) {
            return null;
        }

        // If userId provided, check if this user has already used this code
        if (userId) {
            const { data: usageData, error: usageError } = await supabase
                .from(TABLES.INVITE_CODE_USAGE)
                .select('id')
                .eq('invite_code_id', data.id)
                .eq('used_by', userId)
                .single();

            if (usageError && usageError.code !== 'PGRST116') {
                throw usageError;
            }

            // If usage record exists, user has already used this code
            if (usageData) {
                return null;
            }
        }

        return data;
    } catch (error) {
        console.error('Error validating invite code:', error);
        return null;
    }
};

export const recordInviteCodeUsage = async (inviteCodeId: string, userId: string): Promise<boolean> => {
    if (isTestMode) {
        console.log('Test mode: Invite code usage would be recorded');
        return true;
    }

    try {
        // Start a transaction to ensure consistency
        const { error: usageError } = await supabase
            .from(TABLES.INVITE_CODE_USAGE)
            .insert([{
                invite_code_id: inviteCodeId,
                used_by: userId
            }]);

        if (usageError) throw usageError;

        // Increment the current_uses counter using the database function
        const { error: updateError } = await supabase
            .rpc('increment_invite_code_usage', { invite_code_id: inviteCodeId });

        if (updateError) {
            // If RPC function doesn't exist, get current count and increment manually
            const { data: currentData, error: fetchError } = await supabase
                .from(TABLES.INVITE_CODES)
                .select('current_uses')
                .eq('id', inviteCodeId)
                .single();

            if (fetchError) throw fetchError;

            const { error: fallbackError } = await supabase
                .from(TABLES.INVITE_CODES)
                .update({ current_uses: (currentData.current_uses || 0) + 1 })
                .eq('id', inviteCodeId);

            if (fallbackError) throw fallbackError;
        }

        return true;
    } catch (error) {
        console.error('Error recording invite code usage:', error);
        return false;
    }
};

export const signUpWithInviteCode = async (email: string, password: string, inviteCode: string) => {
    if (isTestMode) {
        // Mock sign up for test mode
        return {
            user: {
                id: 'test-user-id',
                email: email,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            },
            session: null,
        };
    }

    // Sign up the user first
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;

    if (!data.user) {
        throw new Error('User creation failed');
    }

    // Validate the invite code with the user ID
    const validInvite = await validateInviteCode(inviteCode, data.user.id);
    if (!validInvite) {
        // If invite code is invalid, we should ideally roll back the user creation
        // For now, we'll just throw an error
        throw new Error('Invalid or expired invite code, or you have already used this code');
    }

    // Record the invite code usage
    const recorded = await recordInviteCodeUsage(validInvite.id, data.user.id);
    if (!recorded) {
        throw new Error('Failed to record invite code usage');
    }

    // Check if user was on waitlist and mark as converted
    await markWaitlistUserConverted(email, data.user.id, 'invite_code');

    return data;
};

// Waitlist Functions
export const joinWaitlist = async (email: string): Promise<boolean> => {
    if (isTestMode) {
        console.log('Test mode: Email would be added to waitlist:', email);
        return true;
    }

    try {
        const { error } = await supabase
            .from(TABLES.WAITLIST)
            .insert([{ email: email.toLowerCase() }]);

        if (error) {
            // Handle duplicate email case
            if (error.code === '23505') {
                throw new Error('This email is already on the waitlist');
            }
            throw error;
        }

        return true;
    } catch (error) {
        console.error('Error joining waitlist:', error);
        throw error;
    }
};

export const checkWaitlistStatus = async (email: string): Promise<WaitlistEntry | null> => {
    if (isTestMode) {
        return {
            id: 'test-waitlist-id',
            email: email,
            created_at: new Date().toISOString(),
        };
    }

    try {
        const { data, error } = await supabase
            .from(TABLES.WAITLIST)
            .select('*')
            .eq('email', email.toLowerCase())
            .single();

        if (error) {
            if (error.code === 'PGRST116') { // No rows returned
                return null;
            }
            throw error;
        }

        return data;
    } catch (error) {
        console.error('Error checking waitlist status:', error);
        return null;
    }
};

// Waitlist Conversion Tracking Functions
export const markWaitlistUserConverted = async (
    email: string,
    userId: string,
    signupMethod: string = 'email_password'
): Promise<boolean> => {
    if (isTestMode) {
        console.log('Test mode: Waitlist user conversion would be tracked:', email, signupMethod);
        return true;
    }

    try {
        const { error } = await supabase
            .rpc('mark_waitlist_user_converted', {
                user_email: email.toLowerCase(),
                auth_user_id: userId,
                signup_method_param: signupMethod
            });

        if (error) {
            console.error('Error marking waitlist user as converted:', error);
            return false;
        }

        return true;
    } catch (error) {
        console.error('Error marking waitlist user as converted:', error);
        return false;
    }
};

export const getWaitlistAnalytics = async () => {
    if (isTestMode) {
        return {
            total_waitlist_users: 10,
            converted_users: 3,
            pending_users: 7,
            conversion_rate_percent: 30.0,
            converted_via_invite: 2,
            converted_via_google: 1,
            converted_via_email: 0,
        };
    }

    try {
        const { data, error } = await supabase
            .from('waitlist_analytics')
            .select('*')
            .single();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error fetching waitlist analytics:', error);
        return null;
    }
};

export const getWaitlistUserDetails = async (limit: number = 50) => {
    if (isTestMode) {
        return [
            {
                email: 'test1@example.com',
                joined_waitlist: new Date().toISOString(),
                has_signed_up: true,
                signed_up_at: new Date().toISOString(),
                signup_method: 'invite_code',
                status: 'CONVERTED',
                days_to_convert: 2,
            },
            {
                email: 'test2@example.com',
                joined_waitlist: new Date().toISOString(),
                has_signed_up: false,
                status: 'WAITING',
                days_to_convert: null,
            },
        ];
    }

    try {
        const { data, error } = await supabase
            .from('waitlist_user_details')
            .select('*')
            .limit(limit);

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error fetching waitlist user details:', error);
        return [];
    }
};

// Personal Context Entries Functions
export const getPersonalContextEntries = async (assistantId: string): Promise<PersonalContextEntry[]> => {
    if (isTestMode) {
        return [
            {
                id: '1',
                assistant_id: assistantId,
                title: 'Bio',
                content: 'Professional software engineer and AI enthusiast with 10+ years of experience in web development.',
                description: 'Your professional background and expertise',
                prompt: 'Tell me about your professional background and what you do.',
                display_order: 1,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            },
            {
                id: '2',
                assistant_id: assistantId,
                title: 'Experience & Expertise',
                content: 'Full-stack developer specializing in React, Node.js, and cloud technologies. Passionate about creating innovative AI solutions.',
                description: 'Your key skills and areas of expertise',
                prompt: 'What are your main skills and areas of expertise?',
                display_order: 2,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            },
            {
                id: '3',
                assistant_id: assistantId,
                title: 'Testimonials',
                content: 'Lauren is an excellent developer who consistently delivers high-quality work and brings creative solutions to complex problems.',
                description: 'What others say about your work',
                prompt: 'Share testimonials or feedback from others about your work.',
                display_order: 3,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            },
        ];
    }

    try {
        const { data, error } = await supabase
            .from('personal_context_entries')
            .select('*')
            .eq('assistant_id', assistantId)
            .order('display_order', { ascending: true })
            .order('created_at', { ascending: true });

        if (error) throw error;
        return data || [];
    } catch (error) {
        console.error('Error fetching personal context entries:', error);
        return [];
    }
};

export const createPersonalContextEntry = async (
    assistantId: string,
    entryData: Omit<PersonalContextEntry, 'id' | 'assistant_id' | 'created_at' | 'updated_at'>
): Promise<PersonalContextEntry | null> => {
    if (isTestMode) {
        console.log('Test mode: Personal context entry would be created');
        return {
            id: 'new-entry-id',
            assistant_id: assistantId,
            title: entryData.title,
            content: entryData.content,
            prompt: entryData.prompt,
            description: entryData.description,
            display_order: entryData.display_order,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        };
    }

    try {
        const { data, error } = await supabase
            .from('personal_context_entries')
            .insert([{
                assistant_id: assistantId,
                title: entryData.title,
                content: entryData.content,
                prompt: entryData.prompt,
                description: entryData.description,
                display_order: entryData.display_order || 0,
            }])
            .select()
            .single();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error creating personal context entry:', error);
        return null;
    }
};

export const createPersonalContextEntriesFromCategories = async (
    assistantId: string,
    categories: Array<{ title: string; description: string; prompt: string }>
): Promise<PersonalContextEntry[]> => {
    if (isTestMode) {
        console.log('Test mode: Personal context entries would be created from categories');
        return categories.map((category, index) => ({
            id: `category-${index + 1}`,
            assistant_id: assistantId,
            title: category.title,
            content: '', // Empty content for user to fill in
            prompt: category.prompt, // Store the prompt to help users
            description: category.description, // Store the description
            display_order: index + 1,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        }));
    }

    try {
        const entries = [];
        for (let i = 0; i < categories.length; i++) {
            const category = categories[i];
            const entry = await createPersonalContextEntry(assistantId, {
                title: category.title,
                content: '', // Empty content for user to fill in
                prompt: category.prompt, // Store the prompt to help users
                description: category.description, // Store the description
                display_order: i + 1,
            });
            if (entry) {
                entries.push(entry);
            }
        }
        return entries;
    } catch (error) {
        console.error('Error creating personal context entries from categories:', error);
        return [];
    }
};

export const updatePersonalContextEntry = async (
    entryId: string,
    entryData: Partial<Omit<PersonalContextEntry, 'id' | 'assistant_id' | 'created_at' | 'updated_at'>>
): Promise<PersonalContextEntry | null> => {
    if (isTestMode) {
        console.log('Test mode: Personal context entry would be updated');
        return {
            id: entryId,
            assistant_id: 'test-assistant-id',
            title: entryData.title || 'Updated Title',
            content: entryData.content || 'Updated content',
            description: entryData.description,
            prompt: entryData.prompt,
            display_order: entryData.display_order || 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        };
    }

    try {
        const { data, error } = await supabase
            .from('personal_context_entries')
            .update(entryData)
            .eq('id', entryId)
            .select('id, assistant_id, title, content, description, prompt, display_order, created_at, updated_at')
            .single();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error updating personal context entry:', error);
        return null;
    }
};

export const deletePersonalContextEntry = async (entryId: string): Promise<boolean> => {
    if (isTestMode) {
        console.log('Test mode: Personal context entry would be deleted');
        return true;
    }

    try {
        const { error } = await supabase
            .from('personal_context_entries')
            .delete()
            .eq('id', entryId);

        if (error) throw error;
        return true;
    } catch (error) {
        console.error('Error deleting personal context entry:', error);
        return false;
    }
};

export const reorderPersonalContextEntries = async (
    assistantId: string,
    entryIds: string[]
): Promise<boolean> => {
    if (isTestMode) {
        console.log('Test mode: Personal context entries would be reordered');
        return true;
    }

    try {
        const updates = entryIds.map((id, index) => ({
            id,
            display_order: index + 1,
        }));

        const { error } = await supabase
            .from('personal_context_entries')
            .upsert(updates, { onConflict: 'id' });

        if (error) throw error;
        return true;
    } catch (error) {
        console.error('Error reordering personal context entries:', error);
        return false;
    }
};

// Helper function to generate a hash of assistant content for cache invalidation
const generateContentHash = async (assistant: Assistant): Promise<string> => {
    // Get personal context entries for content generation
    const personalContextEntries = await getPersonalContextEntries(assistant.id);
    const contextContent = personalContextEntries.map(entry => `${entry.title}: ${entry.content}`).join('\n\n');
    const content = `${assistant.name}${assistant.pronouns}${contextContent}`;
    console.log('Generating content hash for', assistant.name, 'with', personalContextEntries.length, 'entries');
    // Simple hash function - in production, you might want to use a more robust hashing library
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
        const char = content.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString();
};

// Get cached context cards for an assistant
export const getCachedContextCards = async (assistantId: string): Promise<ContextCard[] | null> => {
    if (isTestMode) {
        return null; // Don't use cache in test mode
    }

    try {
        const { data, error } = await supabase
            .from(TABLES.CONTEXT_CARDS_CACHE)
            .select('cards, content_hash')
            .eq('assistant_id', assistantId)
            .single();

        if (error) {
            if (error.code === 'PGRST116') {
                // No cache found
                return null;
            }
            console.error('Error fetching cached context cards:', error);
            return null;
        }

        return data.cards;
    } catch (error) {
        console.error('Error fetching cached context cards:', error);
        return null;
    }
};

// Save context cards to cache
export const saveContextCardsToCache = async (
    assistantId: string,
    cards: ContextCard[],
    assistant: Assistant
): Promise<void> => {
    if (isTestMode) {
        console.log('Test mode: Skipping context cards cache save');
        return; // Don't save cache in test mode
    }

    try {
        const contentHash = await generateContentHash(assistant);

        const { error } = await supabase
            .from(TABLES.CONTEXT_CARDS_CACHE)
            .upsert({
                assistant_id: assistantId,
                cards: cards,
                content_hash: contentHash,
                updated_at: new Date().toISOString()
            }, {
                onConflict: 'assistant_id'
            });

        if (error) {
            console.error('Error saving context cards to cache:', error);
        }
    } catch (error) {
        console.error('Error saving context cards to cache:', error);
    }
};

// Check if context cards need to be regenerated
export const shouldRegenerateContextCards = async (
    assistantId: string,
    assistant: Assistant
): Promise<boolean> => {
    if (isTestMode) {
        return true; // Always regenerate in test mode
    }

    try {
        const { data, error } = await supabase
            .from(TABLES.CONTEXT_CARDS_CACHE)
            .select('content_hash')
            .eq('assistant_id', assistantId)
            .single();

        if (error) {
            if (error.code === 'PGRST116') {
                // No cache found, need to generate
                console.log('No cache found for assistant', assistantId, '- will regenerate');
                return true;
            }
            console.error('Error checking context cards cache:', error);
            return true;
        }

        const currentHash = await generateContentHash(assistant);
        const needsRegeneration = data.content_hash !== currentHash;
        console.log('Cache check for assistant', assistantId, ':', {
            cachedHash: data.content_hash,
            currentHash: currentHash,
            needsRegeneration: needsRegeneration
        });
        return needsRegeneration;
    } catch (error) {
        console.error('Error checking context cards cache:', error);
        return true;
    }
};

// Clear context cards cache for an assistant (called when content is updated)
export const clearContextCardsCache = async (assistantId: string): Promise<void> => {
    if (isTestMode) {
        console.log('Test mode: Skipping context cards cache clear');
        return; // Don't clear cache in test mode
    }

    try {
        const { error } = await supabase
            .from(TABLES.CONTEXT_CARDS_CACHE)
            .delete()
            .eq('assistant_id', assistantId);

        if (error) {
            console.error('Error clearing context cards cache:', error);
        }
    } catch (error) {
        console.error('Error clearing context cards cache:', error);
    }
};
